import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  TextField,
  Snackbar,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Editor } from '@tiptap/react';
import { useTranslation } from 'react-i18next';
import { borderRadius, palette, spaces } from '../../assets/styles/themes/tokens';
import katex from 'katex';
import DOMPurify from 'dompurify';

interface MathEditorDialogProps {
  open: boolean;
  onClose: () => void;
  editor: Editor | null;
  initialLatex: string;
  pos: number;
  isBlock: boolean;
}

const MathEditorDialog: React.FC<MathEditorDialogProps> = ({
  open,
  onClose,
  editor,
  initialLatex,
  pos,
  isBlock: initialIsBlock,
}) => {
  const { t } = useTranslation();
  const [latex, setLatex] = useState(initialLatex);
  const [mathType, setMathType] = useState<'inline' | 'block'>(initialIsBlock ? 'block' : 'inline');
  const [renderError, setRenderError] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (open) {
      setLatex(initialLatex);
      setMathType(initialIsBlock ? 'block' : 'inline');
      setRenderError(null);
    }
  }, [open, initialLatex, initialIsBlock]);

  const handleSave = () => {
    if (!editor || !latex.trim()) {
      setShowError(true);
      return;
    }

    try {
      // Set node selection to the math node first
      editor.chain().setNodeSelection(pos).run();

      // Update or replace based on type change
      if (mathType === 'block' && initialIsBlock) {
        editor.chain().updateBlockMath({ latex }).focus().run();
      } else if (mathType === 'inline' && !initialIsBlock) {
        editor.chain().updateInlineMath({ latex }).focus().run();
      } else {
        // Type changed - delete old node and insert new one
        editor.chain().deleteSelection().run();
        if (mathType === 'block') {
          editor.chain().insertBlockMath({ latex }).focus().run();
        } else {
          editor.chain().insertInlineMath({ latex }).focus().run();
        }
      }

      onClose();
    } catch (error) {
      setShowError(true);
    }
  };

  const handleCancel = () => {
    if (!editor) {
      onClose();
      return;
    }
    
    // If the initial latex was just whitespace (placeholder from slash command), delete the node
    if (initialLatex.trim() === '') {
      editor.chain().setNodeSelection(pos).deleteSelection().focus().run();
    } else {
      // For existing formulas, move cursor to after the node to deselect it
      const node = editor.state.doc.nodeAt(pos);
      if (node) {
        // Set cursor position to after the math node
        const endPos = pos + node.nodeSize;
        editor.chain().setTextSelection(endPos).focus().run();
      } else {
        editor.commands.focus();
      }
    }
    
    setLatex(initialLatex);
    setMathType(initialIsBlock ? 'block' : 'inline');
    setRenderError(null);
    onClose();
  };

  // Compute preview rendering and error state
  const preview = useMemo(() => {
    if (!latex.trim()) {
      return {
        element: (
          <Box
            sx={{
              border: `1px solid ${palette.divider}`,
              borderRadius: borderRadius.md,
              padding: spaces.md,
              backgroundColor: palette.background.paperElevation2,
              minHeight: '80px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography variant='body2' color='text.secondary'>
              {t('Enter LaTeX to see preview')}
            </Typography>
          </Box>
        ),
        error: null,
      };
    }

    try {
      const html = katex.renderToString(latex, {
        displayMode: mathType === 'block',
        throwOnError: true,
        strict: 'warn',
      });

      // Sanitize KaTeX output as defense-in-depth against potential XSS
      const sanitized = DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [
          'span', 'annotation', 'semantics', 'math', 'mrow', 'mi', 'mo', 'mn',
          'msup', 'msub', 'mfrac', 'munder', 'mover', 'munderover', 'msqrt',
          'mroot', 'mtable', 'mtr', 'mtd', 'mtext', 'mspace', 'mpadded',
          'menclose', 'mglyph', 'svg', 'path', 'line', 'rect',
        ],
        ALLOWED_ATTR: ['class', 'style', 'encoding', 'xmlns', 'mathvariant',
          'width', 'height', 'viewBox', 'preserveAspectRatio', 'fill', 'stroke',
          'd', 'x', 'y', 'x1', 'y1', 'x2', 'y2',
        ],
      });

      return {
        element: (
          <Box
            sx={{
              border: `1px solid ${palette.divider}`,
              borderRadius: borderRadius.md,
              padding: spaces.md,
              backgroundColor: palette.background.paperElevation2,
              minHeight: '80px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
            dangerouslySetInnerHTML={{ __html: sanitized }}
          />
        ),
        error: null,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('Invalid LaTeX syntax');

      return {
        element: (
          <Box
            sx={{
              border: `1px solid ${palette.error.main}`,
              borderRadius: borderRadius.md,
              padding: spaces.md,
              backgroundColor: palette.background.paperElevation2,
              minHeight: '80px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Typography variant='body2' color='error'>
              {errorMessage}
            </Typography>
          </Box>
        ),
        error: errorMessage,
      };
    }
  }, [latex, mathType, t]);

  // Update renderError state when preview error changes
  useEffect(() => {
    setRenderError(preview.error);
  }, [preview.error]);

  return (
    <>
      <Dialog
        open={open}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') {
            return;
          }
          handleCancel();
        }}
        maxWidth={false}
        sx={{
          zIndex: 1500,
        }}
        PaperProps={{
          sx: {
            width: '700px',
            maxWidth: '90vw',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: spaces.lg,
            gap: spaces.md,
          }}
        >
          <Typography variant='h5' component='span'>
            {t('Edit Math Formula')}
          </Typography>
          <IconButton
            aria-label='close'
            onClick={handleCancel}
            size='small'
            sx={{
              padding: spaces.xs,
            }}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            paddingX: spaces.lg,
            paddingBottom: spaces.md,
            paddingTop: 0,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.lg }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.md }}>
              <Typography variant='body1' color='text.secondary'>
                {t('Math Type')}
              </Typography>
              <ToggleButtonGroup
                value={mathType}
                exclusive
                onChange={(_e, value) => value && setMathType(value)}
                size='small'
                fullWidth
              >
                <ToggleButton value='inline'>{t('Inline')}</ToggleButton>
                <ToggleButton value='block'>{t('Block')}</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.md }}>
              <Typography variant='body1' color='text.secondary'>
                {t('LaTeX')}
              </Typography>
              <TextField
                multiline
                rows={4}
                value={latex}
                onChange={(e) => setLatex(e.target.value)}
                placeholder={t('Enter LaTeX formula (e.g., \\frac{a}{b} or \\sum_{i=1}^{n} x_i)')}
                fullWidth
                autoFocus
                error={!!renderError}
                helperText={renderError}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.md }}>
              <Typography variant='body1' color='text.secondary'>
                {t('Preview')}
              </Typography>
              {preview.element}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: spaces.xs,
            padding: spaces.lg,
          }}
        >
          <Button
            variant='outlined'
            size='large'
            onClick={handleCancel}
            sx={{ flex: 1 }}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant='contained'
            size='large'
            onClick={handleSave}
            disabled={!editor || !latex.trim() || !!renderError}
            sx={{ flex: 1 }}
          >
            {t('Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowError(false)}
          severity='error'
          sx={{ width: '100%' }}
        >
          {t('Math formula could not be saved. Please try again.')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MathEditorDialog;
